import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { formatBRL } from "@/lib/constants";
import { Receipt, Search, ExternalLink, Download, MessageCircle, Loader2 } from "lucide-react";

export default function Recibos() {
  const [items, setItems] = useState(null);
  const [clients, setClients] = useState({});
  const [q, setQ] = useState("");
  const [period, setPeriod] = useState("all");

  useEffect(() => {
    Promise.all([api.get("/documents", { params: { type: "recibo" } }), api.get("/clients")])
      .then(([d, c]) => {
        setItems(d.data);
        setClients(Object.fromEntries((c.data || []).map((x) => [x.id, x])));
      })
      .catch(() => setItems([]));
  }, []);

  const periods = useMemo(() => {
    if (!items) return [];
    const set = new Set();
    items.forEach((r) => { const m = (r.date || "").match(/^(\d{2})\/(\d{2})\/(\d{4})$/); if (m) set.add(`${m[2]}/${m[3]}`); });
    return Array.from(set).sort((a, b) => { const [ma, ya] = a.split("/"); const [mb, yb] = b.split("/"); return (yb + mb).localeCompare(ya + ma); });
  }, [items]);

  const MONTHS = ["", "jan", "fev", "mar", "abr", "mai", "jun", "jul", "ago", "set", "out", "nov", "dez"];
  const periodLabel = (p) => { const [m, y] = p.split("/"); return `${MONTHS[Number(m)]}/${y}`; };

  const filtered = useMemo(() => {
    if (!items) return [];
    const s = q.trim().toLowerCase();
    return items.filter((r) => {
      if (period !== "all") { const m = (r.date || "").match(/^(\d{2})\/(\d{2})\/(\d{4})$/); if (!m || `${m[2]}/${m[3]}` !== period) return false; }
      if (!s) return true;
      return (r.client_name || "").toLowerCase().includes(s) || (r.number || "").toLowerCase().includes(s);
    });
  }, [items, q, period]);

  const total = useMemo(() => filtered.reduce((sum, r) => sum + Number(r.total || 0), 0), [filtered]);

  const openOnline = (r) => window.open(`${window.location.origin}/doc/${r.share_token}`, "_blank");

  const downloadPdf = async (r) => {
    try {
      const { data } = await api.get(`/documents/${r.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${r.number}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Erro ao baixar o PDF");
    }
  };

  const sendWa = (r) => {
    const cl = clients[r.client_id] || {};
    const wnum = (cl.whatsapp || cl.phone || "").replace(/\D/g, "");
    if (!wnum) {
      toast.error("Cliente sem WhatsApp cadastrado");
      return;
    }
    const link = `${window.location.origin}/doc/${r.share_token}`;
    const msg = `Olá ${r.client_name}! 😊 Segue o seu recibo ${r.number}: ${link}`;
    window.open(`https://wa.me/${wnum}?text=${encodeURIComponent(msg)}`, "_blank");
  };

  if (!items) return <div className="h-40 flex items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>;

  return (
    <div className="space-y-5">
      <div>
        <h1 className="font-display font-extrabold text-2xl sm:text-3xl flex items-center gap-2"><Receipt className="h-6 w-6 text-primary" /> Recibos</h1>
        <p className="text-muted-foreground text-sm">Histórico de todos os recibos emitidos. Busque por cliente e reenvie por WhatsApp.</p>
      </div>

      <Card className="p-4 flex items-center justify-between" data-testid="recibos-total-card">
        <div><p className="text-sm text-muted-foreground">{filtered.length} recibo(s)</p><p className="text-2xl font-display font-extrabold text-secondary">{formatBRL(total)}</p></div>
        <div className="h-11 w-11 rounded-xl bg-secondary/10 text-secondary flex items-center justify-center"><Receipt className="h-5 w-5" /></div>
      </Card>

      <div className="flex flex-col sm:flex-row gap-2">
        <div className="relative flex-1 max-w-md">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar por cliente ou número..." className="pl-9 h-11" data-testid="recibos-search" />
        </div>
        <Select value={period} onValueChange={setPeriod}>
          <SelectTrigger className="h-11 w-full sm:w-44" data-testid="recibos-period"><SelectValue placeholder="Período" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos os períodos</SelectItem>
            {periods.map((p) => <SelectItem key={p} value={p}>{periodLabel(p)}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {filtered.length === 0 ? (
        <Card className="p-10 text-center text-muted-foreground"><Receipt className="h-10 w-10 mx-auto mb-2 opacity-40" /><p>Nenhum recibo encontrado. Recibos são gerados automaticamente ao marcar um documento como PAGO.</p></Card>
      ) : (
        <div className="space-y-2">
          {filtered.map((r) => (
            <Card key={r.id} className="p-4 flex items-center gap-3 flex-wrap" data-testid={`recibo-${r.id}`}>
              <div className="min-w-0 flex-1">
                <p className="font-semibold truncate">{r.client_name}</p>
                <p className="text-xs text-muted-foreground truncate">{r.number}{r.date ? ` • ${r.date}` : ""}</p>
              </div>
              <div className="text-right shrink-0">
                <p className="font-display font-bold text-secondary">{formatBRL(r.total)}</p>
                <Badge variant="outline" className="text-[10px] bg-emerald-100 text-emerald-700 border-emerald-200">PAGO</Badge>
              </div>
              <Button size="sm" variant="outline" onClick={() => openOnline(r)} data-testid={`recibo-open-${r.id}`}><ExternalLink className="h-4 w-4" /></Button>
              <Button size="sm" variant="outline" onClick={() => downloadPdf(r)} data-testid={`recibo-pdf-${r.id}`}><Download className="h-4 w-4" /></Button>
              <Button size="sm" className="bg-[#25D366] hover:bg-[#1da851]" onClick={() => sendWa(r)} data-testid={`recibo-wa-${r.id}`}><MessageCircle className="h-4 w-4" /></Button>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
